import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  postMessage,
  sendEmailNotification,
  fetchMessages,
} from 'src/store/actions/Messages';
import { selectMessages } from 'src/store/selectors/Messages';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { upload } from 'src/helpers/upLoadHelper';
import Loader from '../../components/Loader';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { fetchSuiteCounts } from 'src/store/actions/suites';
import { PATHS } from 'src/common/constants';
import { Messages } from 'src/components/Messages/Messages';
import { ROLES } from 'src/utils/MessagingUtils/shared-constants';

const EMPTY_UUID = '00000000-0000-0000-0000-000000000000';

interface SuiteMessagesProps {
  suite: any;
  property: any;
  url: any;
  history: any;
}

interface MessageRecipient {
  id: string | null;
  error?: string;
}

const SuiteMessages = ({
  suite,
  property,
  url,
  history,
}: SuiteMessagesProps) => {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthenticatedUser);
  const messages = useSelector(selectMessages);
  const loader = useSelector(selectFormLoader);
  const inboxLoader = useSelector(
    (state: any) => state.inbox?.inboxListLoader || false
  );

  useEffect(() => {
    const data = {
      ReferenceId: suite.id,
      ReferenceType: 'Suite',
    };
    dispatch(fetchMessages(data) as any);
  }, [dispatch, suite.id]);

  const getMessageRecipient = (): MessageRecipient => {
    const userRole = user?.roles?.[0]?.role?.name;

    if (!userRole) {
      return {
        id: null,
        error: 'User role not found. Please contact support.',
      };
    }

    switch (userRole) {
      case ROLES.PROPERTY_DEVELOPER:
        return getHomeOwnerRecipient();
      case ROLES.HOME_OWNER:
      case ROLES.ADMIN:
        return getPropertyManagerRecipient();
      default:
        return {
          id: null,
          error: 'Your user role is not authorized to send messages.',
        };
    }
  };

  const getHomeOwnerRecipient = (): MessageRecipient => {
    const homeOwnerId = suite?.homeOwners?.id;

    if (!homeOwnerId || homeOwnerId === EMPTY_UUID) {
      return {
        id: null,
        error:
          'There is no homeowner associated with this suite. Please contact your admin to have a homeowner assigned before sending messages.',
      };
    }

    return { id: homeOwnerId };
  };

  const getPropertyManagerRecipient = (): MessageRecipient => {
    const propertyManagerId = property?.propertyManager?.id;

    if (!propertyManagerId || propertyManagerId === EMPTY_UUID) {
      return {
        id: null,
        error:
          'There is no property manager assigned to this property. Please contact your admin to have a property manager assigned before sending messages.',
      };
    }

    return { id: propertyManagerId };
  };

  const extractFileName = (imageUrl: string): string => {
    const urlParts: string[] = imageUrl.split('.com/');
    return urlParts.length > 1 ? urlParts[1] : '';
  };

  const onPostMessage = async (chat: any): Promise<void> => {
    try {
      let imageUrl: string | null = null;

      if (chat.image?.length > 0) {
        imageUrl = await upload(chat.image[0]);
      }

      const recipient: MessageRecipient = getMessageRecipient();

      if (!recipient.id) {
        toast.error(
          recipient.error || 'Unable to send message. Please try again.',
          {
            autoClose: 5000,
            position: 'top-center',
          }
        );
        return;
      }

      // Prepare message data.
      const messageData = {
        SentBy: user.id,
        SentTo: recipient.id,
        ReferenceId: suite.id,
        ReferenceType: 'Suite',
        Message: chat.message,
        FileName: imageUrl ? extractFileName(imageUrl) : '',
        FileURL: imageUrl,
        ActionUrl: `app/suites/${suite.propertyId}&${suite.id}/activeTab=4`,
      };

      // Send message and update suite counts.
      await dispatch(postMessage(messageData) as any);
      dispatch(fetchSuiteCounts(suite.id, user.roles[0].role.name) as any);
    } catch (error) {
      console.error('Error posting message:', error);
      toast.error('Failed to send message. Please try again.', {
        autoClose: 5000,
        position: 'top-center',
      });
    }
  };

  const onSendEmailNotification = (data: any): void => {
    try {
      const chatId = suite.number?.replace(/\s/g, '') || '';
      const senderName = data.sentByName?.replace(/\s/g, '') || '';

      const navigationPath = {
        pathname: PATHS.VIDEO_CHAT,
        state: {
          chatId: `channel${chatId}`,
          sentByName: data.sentByName,
          sentToName: data.sentToName,
          url: url,
          activeTab: 3,
        },
      };

      const emailParams = {
        SentBy: data.sentTo,
        SentTo: data.sentBy,
        ChannelName: `channel${chatId}`,
        ActionUrl: `app/videoChat/chatId=channel${chatId}/${senderName}`,
        PropertyName: property?.name || '',
        SuiteName: suite?.number || '',
      };

      dispatch(
        sendEmailNotification(emailParams, navigationPath, history) as any
      );
    } catch (error) {
      console.error('Error sending email notification:', error);
      toast.error('Failed to send email notification. Please try again.', {
        autoClose: 5000,
        position: 'top-center',
      });
    }
  };

  // Prepare chat ID for Messages component
  const chatId = suite.number?.replace(/\s/g, '') || '';

  return loader ? (
    <Loader />
  ) : (
    <>
      {inboxLoader && <Loader center={true} />}
      <Messages
        ticketId={chatId}
        messages={messages}
        url={url}
        onPostMessage={onPostMessage}
        onSendEmailNotification={onSendEmailNotification}
        serviceRequestId={null}
      />
    </>
  );
};

export default SuiteMessages;
